import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import authClient from '../utils/authClient';
import FeedbackManagement from '../components/FeedbackManagement';
import UserManagement from '../components/UserManagement';
import OralHistoryManagement from '../components/OralHistoryManagement';
import GameSubmissionGuide from '../components/GameSubmissionGuide';
import './Manage.css';
import '../utils/loadingStyles.css';

const Manage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      if (!authClient.isAuthenticated()) {
        // Redirect to login page with current location for redirect after login
        navigate('/login', { state: { from: { pathname: '/manage' } } });
      } else {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Initialize activeTab state with value from localStorage or default to 'games'
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('manageActiveTab') || 'games';
    }
    return 'games';
  });
  
  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('manageActiveTab', activeTab);
    }
  }, [activeTab]);

  const [games, setGames] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [isCompressing, setIsCompressing] = useState(false);
  const [gameTags, setGameTags] = useState({});
  const [showOnlyMyGames, setShowOnlyMyGames] = useState(false);
  const [adminUsers, setAdminUsers] = useState({});
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  // Add loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Add a retry counter
  const [retryCount, setRetryCount] = useState(0);
  // Version management states
  const [versionHistory, setVersionHistory] = useState([]);
  const [todoList, setTodoList] = useState([]);
  const [newTodoItem, setNewTodoItem] = useState('');
  const [newChangeItem, setNewChangeItem] = useState('');
  const [changeItems, setChangeItems] = useState([]);
  // Onboarding queue states
  const [onboardingQueue, setOnboardingQueue] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [searchGameTerm, setSearchGameTerm] = useState('');
  // Volunteer applications states
  const [applications, setApplications] = useState([]);
  const [expandedApplications, setExpandedApplications] = useState({});
  // Game applications states
  const [gameApplications, setGameApplications] = useState([]);
  const [expandedGameApps, setExpandedGameApps] = useState({});
  // User management states
  const [users, setUsers] = useState([]);
  const [pendingVisitorGames, setPendingVisitorGames] = useState([]);
  const [allVisitorGameSubmissions, setAllVisitorGameSubmissions] = useState([]);
  // navigate is already defined above

  // Helper function to find platform details by ID
  const getPlatformDetails = (platformId) => {
    const platform = platforms.find(p => p.id === parseInt(platformId) || p.id === platformId);
    if (platform) {
      return {
        namechn: platform.namechn || '',
        nameeng: platform.nameeng || '',
      };
    }
    return { namechn: '', nameeng: `ID: ${platformId}` };
  };

  // Add this function to fetch tags for a game
  const fetchGameTags = async (gameId) => {
    try {
      const response = await apiClient.get(`/games/${gameId}/tags`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tags for game ${gameId}:`, error);
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setLoadError(null);
      
      try {
        const promises = [
          apiClient.get("/games"),
          apiClient.get("/platforms"),
          apiClient.get("/users"),
          apiClient.get("/visitor-game-submissions"),
          apiClient.get("/visitor-game-submissions?includeReviewed=1")
        ];
        
        // Only fetch feedback data if we're on the feedback tab
        if (activeTab === 'feedback') {
          try {
            const feedbackResponse = await apiClient.get("/feedback");
            setFeedback(feedbackResponse.data);
          } catch (feedbackError) {
            console.error("Error fetching feedback:", feedbackError);
            setFeedback([]);
            // Add a friendly error message if there's a 404 error (API endpoint doesn't exist)
            if (feedbackError.response && feedbackError.response.status === 404) {
              setLoadError("The feedback system API is not available. Please run the SQL script to create the feedback table first.");
            } else {
              setLoadError(`Failed to load feedback data. ${feedbackError.message}`);
            }
          }
        }
        
        // Fetch version history and todo list if we're on the version tab
        if (activeTab === 'version') {
          try {
            const [versionHistoryResponse, todoListResponse] = await Promise.all([
              apiClient.get("/version-history"),
              apiClient.get("/todo-list")
            ]);
            
            // Ensure version history data is properly formatted
            const versionData = versionHistoryResponse.data.map(version => {
              // Ensure changes is always an array
              let changes = version.changes;
              if (!Array.isArray(changes)) {
                // If not an array, try to convert it to an array
                if (typeof changes === 'string') {
                  changes = changes.split(',').map(item => item.trim()).filter(Boolean);
                } else {
                  changes = [];
                }
              }
              return { ...version, changes };
            });
            
            setVersionHistory(versionData);
            setTodoList(todoListResponse.data);
          } catch (versionError) {
            console.error("Error fetching version management data:", versionError);
            setVersionHistory([]);
            setTodoList([]);
            if (versionError.response && versionError.response.status === 404) {
              setLoadError("The version management API is not available. Please run the SQL script to create the necessary tables first.");
            } else {
              setLoadError(`Failed to load version management data. ${versionError.message}`);
            }
          }
        }
        
        // Fetch onboarding queue if we're on the onboarding tab
        if (activeTab === 'onboarding') {
          try {
            const queueResponse = await apiClient.get("/onboarding-queue");
            setOnboardingQueue(queueResponse.data);
          } catch (queueError) {
            console.error("Error fetching onboarding queue:", queueError);
            setOnboardingQueue([]);
            if (queueError.response && queueError.response.status === 404) {
              setLoadError("The onboarding queue API is not available. Please run the SQL script to create the onboarding_queue table first.");
            } else {
              setLoadError(`Failed to load onboarding queue. ${queueError.message}`);
            }
          }
        }
        
        // Fetch game applications if we're on the game_applications tab
        if (activeTab === 'game_applications') {
          try {
            const gameAppsResponse = await apiClient.get("/game-applications", {
              params: { include_location: 'true' }
            });
            setGameApplications(gameAppsResponse.data);
          } catch (gameAppsError) {
            console.error("Error fetching game applications:", gameAppsError);
            setGameApplications([]);
            if (gameAppsError.response && gameAppsError.response.status === 404) {
              setLoadError("The game applications API is not available. Please run the SQL script to create the game_applications table first.");
            } else {
              setLoadError(`Failed to load game applications. ${gameAppsError.message}`);
            }
          }
        }
        
        // Fetch volunteer applications if we're on the applications tab
        if (activeTab === 'applications') {
          try {
            const applicationsResponse = await apiClient.get("/volunteer-applications");
            setApplications(applicationsResponse.data);
          } catch (applicationsError) {
            console.error("Error fetching volunteer applications:", applicationsError);
            setApplications([]);
            if (applicationsError.response && applicationsError.response.status === 404) {
              setLoadError("The volunteer applications API is not available. Please run the SQL script to create the volunteer_applications table first.");
            } else {
              setLoadError(`Failed to load volunteer applications. ${applicationsError.message}`);
            }
          }
        }
        
        // Load games, platforms, and users data
        const [gamesRes, platformsRes, usersRes, pendingVisitorGamesRes, allVisitorGameSubmissionsRes] = await Promise.all(promises);
        setGames(gamesRes.data);
        setPlatforms(platformsRes.data);
        setPendingVisitorGames(pendingVisitorGamesRes.data);
        setAllVisitorGameSubmissions(allVisitorGameSubmissionsRes.data);
        
        // Store users data
        if (usersRes && usersRes.data) {
          setUsers(usersRes.data);
          
          // Create a mapping of admin IDs to user names
          const usersMap = {};
          usersRes.data.forEach(user => {
            usersMap[user.id] = user.displayName || user.userName;
          });
          setAdminUsers(usersMap);
        }
        
        // Fetch tags for all games
        const tagsPromises = gamesRes.data.map(game => fetchGameTags(game.id));
        const tagsResults = await Promise.all(tagsPromises);
        const tagsMap = {};
        gamesRes.data.forEach((game, index) => {
          tagsMap[game.id] = tagsResults[index];
        });
        setGameTags(tagsMap);
        
        // Reset retry count on success
        setRetryCount(0);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadError(`Failed to load data. ${error.message}`);
        
        // Only retry a few times to avoid infinite loops
        if (retryCount < 3) {
          console.log(`Retrying... Attempt ${retryCount + 1}`);
          setRetryCount(prev => prev + 1);
          // Retry with exponential backoff
          setTimeout(() => {
            fetchData();
          }, 1000 * Math.pow(2, retryCount));
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [retryCount, activeTab]);
  
  // Handle adding a new to-do item
  const handleAddTodo = async () => {
    if (!newTodoItem.trim()) return;
    
    try {
      const response = await apiClient.post('/todo-list', { task: newTodoItem });
      setTodoList([...todoList, response.data]);
      setNewTodoItem('');
    } catch (error) {
      console.error('Error adding todo item:', error);
      alert(`Failed to add todo item: ${error.message}`);
    }
  };
  
  // Handle updating a to-do item status
  const handleUpdateTodoStatus = async (id, status) => {
    try {
      await apiClient.put(`/todo-list/${id}`, { status });
      setTodoList(todoList.map(item => 
        item.id === id ? { ...item, status } : item
      ));
    } catch (error) {
      console.error('Error updating todo status:', error);
      alert(`Failed to update todo status: ${error.message}`);
    }
  };
  
  // Handle adding a change item to the list of changes
  const handleAddChangeItem = () => {
    if (!newChangeItem.trim()) return;
    setChangeItems([...changeItems, newChangeItem]);
    setNewChangeItem('');
  };
  
  // Handle submitting a new version with changes
  const handleSubmitVersion = async () => {
    if (changeItems.length === 0) {
      alert('Please add at least one change item before submitting');
      return;
    }
    
    try {
      const response = await apiClient.post('/version-history', { changes: changeItems });
      setVersionHistory([response.data, ...versionHistory]);
      setChangeItems([]);
    } catch (error) {
      console.error('Error submitting version:', error);
      alert(`Failed to submit version: ${error.message}`);
    }
  };
  
  // Handle removing a change item from the list
  const handleRemoveChangeItem = (index) => {
    const newChangeItems = [...changeItems];
    newChangeItems.splice(index, 1);
    setChangeItems(newChangeItems);
  };

  // Onboarding queue handlers
  const handleAddToQueue = async () => {
    if (!selectedGameId) {
      alert('请选择一个游戏');
      return;
    }
    
    try {
      await apiClient.post('/onboarding-queue', { game_id: selectedGameId });
      // Refresh the queue
      const queueResponse = await apiClient.get('/onboarding-queue');
      setOnboardingQueue(queueResponse.data);
      setSelectedGameId('');
      setSearchGameTerm('');
    } catch (error) {
      console.error('Error adding game to queue:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(`添加失败: ${error.response.data.error}`);
      } else {
        alert(`添加失败: ${error.message}`);
      }
    }
  };

  const handleRemoveFromQueue = async (queueId) => {
    if (!window.confirm('确定要从推荐队列中移除这个游戏吗？')) {
      return;
    }
    
    try {
      await apiClient.delete(`/onboarding-queue/${queueId}`);
      // Refresh the queue
      const queueResponse = await apiClient.get('/onboarding-queue');
      setOnboardingQueue(queueResponse.data);
    } catch (error) {
      console.error('Error removing game from queue:', error);
      alert(`移除失败: ${error.message}`);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const newQueue = [...onboardingQueue];
    [newQueue[index - 1], newQueue[index]] = [newQueue[index], newQueue[index - 1]];
    
    try {
      const orderedIds = newQueue.map(item => item.id);
      await apiClient.put('/onboarding-queue/reorder', { orderedIds });
      setOnboardingQueue(newQueue);
    } catch (error) {
      console.error('Error reordering queue:', error);
      alert(`重新排序失败: ${error.message}`);
    }
  };

  const handleMoveDown = async (index) => {
    if (index === onboardingQueue.length - 1) return;
    
    const newQueue = [...onboardingQueue];
    [newQueue[index], newQueue[index + 1]] = [newQueue[index + 1], newQueue[index]];
    
    try {
      const orderedIds = newQueue.map(item => item.id);
      await apiClient.put('/onboarding-queue/reorder', { orderedIds });
      setOnboardingQueue(newQueue);
    } catch (error) {
      console.error('Error reordering queue:', error);
      alert(`重新排序失败: ${error.message}`);
    }
  };

  // User management handlers
  const handleAddUser = async (userData) => {
    try {
      const response = await apiClient.post('/users', userData);
      setUsers([...users, response.data]);
      alert('用户创建成功！默认密码为 000000');
    } catch (error) {
      console.error('Error adding user:', error);
      alert(error.response?.data?.message || '创建用户失败');
      throw error;
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await apiClient.put(`/users/${userId}`, updates);
      setUsers(users.map(user => user.id === userId ? response.data : user));
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.message || '更新用户失败');
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiClient.delete(`/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
      alert('用户删除成功');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || '删除用户失败');
      throw error;
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      await apiClient.put(`/users/${userId}`, { resetPassword: true });
      // Update the user in the list to show needsPasswordChange
      const response = await apiClient.get(`/users/${userId}`);
      setUsers(users.map(user => user.id === userId ? response.data : user));
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || '重置密码失败');
      throw error;
    }
  };

  // Filter games for selection (exclude already queued games)
  const queuedGameIds = onboardingQueue.map(item => item.game_id);
  const filteredAvailableGames = games.filter(game => {
    const matchesSearch = searchGameTerm === '' || 
      (game.titleeng && game.titleeng.toLowerCase().includes(searchGameTerm.toLowerCase())) ||
      (game.titlechn && game.titlechn.toLowerCase().includes(searchGameTerm.toLowerCase())) ||
      (game.id && game.id.toString().includes(searchGameTerm));
    
    return matchesSearch && !queuedGameIds.includes(game.id);
  });

  // Reset to first page when changing tabs or search term
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const handleDelete = async (id, type) => {
    // Check permissions for non-admins
    if (!authClient.isAdmin()) {
      if (type === 'platforms') {
        alert('您没有权限删除平台');
        return;
      }
      if (type === 'games') {
        const game = games.find(g => g.id === id);
        if (game && game.admin !== currentUser?.id) {
          alert('您只能删除自己创建的游戏');
          return;
        }
      }
    }
    
    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      try {
        await apiClient.delete(`/${type}/${id}`);
        
        if (type === 'games') {
          setGames(games.filter(item => item.id !== id));
        } else if (type === 'platforms') {
          setPlatforms(platforms.filter(item => item.id !== id));
        } else if (type === 'feedback') {
          setFeedback(feedback.filter(item => item.id !== id));
        }
      } catch (err) {
        console.error(`Error deleting ${type}:`, err);
        alert(`Failed to delete ${type}. ${err.message}`);
      }
    }
  };

  const handleUpdateFeedbackStatus = async (id, newStatus) => {
    try {
      await apiClient.put(`/feedback/${id}/status`, { status: newStatus });
      // Update local state to reflect the change
      setFeedback(feedback.map(item => 
        item.id === id ? { ...item, status: newStatus } : item
      ));
    } catch (err) {
      console.error('Error updating feedback status:', err);
      alert(`Failed to update status. ${err.message}`);
    }
  };

  const handleCompress = async (id, type, imagePath, isAlreadyCompressed) => {
    // If already compressed, ask for confirmation
    if (isAlreadyCompressed) {
      if (!window.confirm("This image is already compressed. Compressing again may result in quality loss. Continue?")) {
        return;
      }
    }

    setIsCompressing(true);
    try {
      const response = await apiClient.post("/compress-image", {
        id,
        type,
        filePath: imagePath
      });

      // Update the local state with the new information
      if (type === 'games') {
        setGames(games.map(game => {
          if (game.id === id) {
            return {
              ...game,
              compressed: 1,
              coverSize: response.data.formattedSize
            };
          }
          return game;
        }));
      } else if (type === 'platforms') {
        setPlatforms(platforms.map(platform => {
          if (platform.id === id) {
            return {
              ...platform,
              compressed: 1,
              imgSize: response.data.formattedSize
            };
          }
          return platform;
        }));
      }

      alert("Image compressed successfully!");
    } catch (error) {
      console.error("Error compressing image:", error);
      alert(`Error compressing image: ${error.message}. Please try again.`);
    } finally {
      setIsCompressing(false);
    }
  };

  const toggleDescription = (id, type) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [`${type}-${id}`]: !prev[`${type}-${id}`]
    }));
  };

  // Get current user for filtering
  const currentUser = authClient.getCurrentUser();
  
  // Filter games based on search term and user filter
  const filteredGames = games.filter(game => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (game.titleeng && game.titleeng.toLowerCase().includes(searchLower)) ||
      (game.titlechn && game.titlechn.toLowerCase().includes(searchLower)) ||
      (game.desc && game.desc.toLowerCase().includes(searchLower)) ||
      (game.id && game.id.toString().includes(searchLower))
    );
    
    // Filter by current user if the option is selected
    if (showOnlyMyGames && currentUser && currentUser.id) {
      return matchesSearch && game.admin === currentUser.id;
    }
    
    return matchesSearch;
  }).sort((a, b) => {
    // Sort games by ID number in ascending order
    return parseInt(a.id) - parseInt(b.id);
  });

  const filteredPlatforms = platforms.filter(platform => 
    (platform.nameeng?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (platform.namechn?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Filter feedback items
  const filteredFeedback = feedback.filter(item => 
    (item.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.content?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.type?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.status?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.gameName?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  
  const currentGames = filteredGames.slice(indexOfFirstItem, indexOfLastItem);
  const currentPlatforms = filteredPlatforms.slice(indexOfFirstItem, indexOfLastItem);
  // eslint-disable-next-line no-unused-vars
  const currentFeedback = filteredFeedback.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Go to next page
  const goToNextPage = () => {
    let totalItems;
    
    if (activeTab === 'games') {
      totalItems = filteredGames.length;
    } else if (activeTab === 'platforms') {
      totalItems = filteredPlatforms.length;
    } else {
      totalItems = filteredFeedback.length;
    }
    
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Format feedback type for display
  const getFeedbackTypeDisplay = (type) => {
    const typeMap = {
      'unplayable': '游戏问题',
      'inaccurate': '信息错误',
      'manual_request': '手册请求',
      'new_game': '游戏请求',
      'disassembly_submission': '解编提交'
    };
    
    return typeMap[type] || type;
  };

  // Format feedback status for display
  const getFeedbackStatusDisplay = (status) => {
    const statusMap = {
      'pending': '待处理',
      'resolved': '已解决',
      'rejected': '已拒绝'
    };
    
    return statusMap[status] || status;
  };

  // Status badge color
  const getStatusBadgeClass = (status) => {
    if (status === 'pending') return 'status-pending';
    if (status === 'resolved') return 'status-resolved';
    if (status === 'rejected') return 'status-rejected';
    return '';
  };

  // Pagination component
  const Pagination = ({ totalItems }) => {
    // Don't render pagination in version tab
    if (activeTab === 'version') return null;
    
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const maxPageButtons = 5; // Maximum number of page buttons to show
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    // Adjust start page when near the end
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalItems <= itemsPerPage) return null;

    return (
      <div className="pagination-container">
        <button 
          className="pagination-button" 
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          &#8249;
        </button>
        
        {startPage > 1 && (
          <>
            <button 
              className={`pagination-button ${currentPage === 1 ? 'active' : ''}`}
              onClick={() => paginate(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pageNumbers.map(number => (
          <button 
            key={number}
            className={`pagination-button ${currentPage === number ? 'active' : ''}`}
            onClick={() => paginate(number)}
          >
            {number}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button 
              className={`pagination-button ${currentPage === totalPages ? 'active' : ''}`}
              onClick={() => paginate(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button 
          className="pagination-button" 
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
        >
          &#8250;
        </button>
      </div>
    );
  };

  // Add a card component for mobile view
  const MobileCardView = ({ items, type }) => {
    // Don't render if no items
    if (items.length === 0) return null;

    if (type === 'games') {
      return (
        <div className="mobile-card-view">
          {items.map(game => (
            <div className="mobile-card" key={game.id}>
              <div className="mobile-card-header">
                <span className="mobile-card-id">ID：{game.id}</span>
                <div className="mobile-card-actions">
                  <button 
                    className="edit-button"
                    onClick={() => navigate(`/update/${game.id}`)}
                  >
                    编辑
                  </button>
                  {(authClient.isAdmin() || game.admin === currentUser?.id) && (
                    <button 
                      className="delete-button"
                      onClick={() => handleDelete(game.id, 'games')}
                    >
                      删除
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mobile-card-cover">
                {game.cover && (
                  <div className="mobile-card-image-container">
                    <img 
                      src={`${API_BASE_URL}${game.cover}`} 
                      alt={game.titleeng || game.titlechn}
                      className="mobile-card-image"
                    />
                    <div className="mobile-card-image-details">
                      <div className="file-size">{game.coverSize || 'N/A'}</div>
                      <div className={game.compressed ? 'compressed-badge' : 'not-compressed-badge'}>
                        {game.compressed ? '已压缩' : '未压缩'}
                      </div>
                      {!isCompressing && (
                        <button 
                          className="compress-button"
                          onClick={() => handleCompress(game.id, 'games', game.cover, game.compressed)}
                          disabled={isCompressing}
                        >
                          {game.compressed ? '重新压缩' : '压缩'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mobile-card-content">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">英文标题：</span>
                  <span className="mobile-card-value">{game.titleeng}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">中文标题：</span>
                  <span className="mobile-card-value">{game.titlechn}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">平台：</span>
                  <span className="mobile-card-value">
                    {(() => {
                      const platform = getPlatformDetails(game.platform);
                      return platform.namechn && platform.nameeng 
                        ? `${platform.namechn} (${platform.nameeng})`
                        : platform.namechn || platform.nameeng || `ID: ${game.platform}`;
                    })()}
                  </span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">年份：</span>
                  <span className="mobile-card-value">{game.year}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">开发商：</span>
                  <span className="mobile-card-value">{game.dev}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">浏览量：</span>
                  <div className="view-count mobile-view-count">{game.views || 0}</div>
                </div>
                
                {game.desc && (
                  <div className="mobile-card-field full-width">
                    <span className="mobile-card-label">描述：</span>
                    <div className="description-container">
                      <div className={`description-text ${expandedDescriptions[`games-${game.id}`] ? 'expanded' : ''}`}>
                        {game.desc}
                      </div>
                      {game.desc && game.desc.length > 100 && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleDescription(game.id, 'games')}
                        >
                          {expandedDescriptions[`games-${game.id}`] ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {gameTags[game.id]?.length > 0 && (
                  <div className="mobile-card-field full-width">
                    <span className="mobile-card-label">标签：</span>
                    <div className="game-tags-container mobile-tags">
                      {gameTags[game.id]?.map(tag => (
                        <span 
                          key={tag.id} 
                          className="game-tag"
                          style={{ backgroundColor: tag.color || '#6c757d' }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mobile-card-files">
                {game.manual && (
                  <div className="mobile-card-file-section full-width">
                    <span className="mobile-card-label">手册：</span>
                    <div className="mobile-file-links">
                      {(() => {
                        try {
                          const manuals = JSON.parse(game.manual);
                          
                          if (Array.isArray(manuals) && manuals.length > 0) {
                            return (
                              <>
                                {manuals.map((manual, index) => (
                                  <div key={index} className="mobile-file-link">
                                    <a 
                                      href={`${API_BASE_URL}${manual.path}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="file-button"
                                    >
                                      {manual.name || `手册 ${index + 1}`}
                                    </a>
                                    <div className="file-size">{game.manualSizes?.[index] || 'N/A'}</div>
                                  </div>
                                ))}
                              </>
                            );
                          }
                        } catch (e) {
                          console.error('Error parsing manual JSON:', e);
                        }
                        
                        return null;
                      })()}
                    </div>
                  </div>
                )}
                
                {game.rom && (
                  <div className="mobile-card-file-section full-width">
                    <span className="mobile-card-label">ROM文件：</span>
                    <div className="mobile-file-links">
                      {(() => {
                        try {
                          const roms = JSON.parse(game.rom);
                          
                          if (Array.isArray(roms) && roms.length > 0) {
                            return (
                              <>
                                {roms.map((rom, index) => (
                                  <div key={index} className="mobile-file-link">
                                    <a 
                                      href={`${API_BASE_URL}${rom.path}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="file-button"
                                    >
                                      {rom.type}
                                    </a>
                                    <div className="file-size">{game.romSizes?.[index] || 'N/A'}</div>
                                  </div>
                                ))}
                              </>
                            );
                          }
                        } catch (e) {
                          console.error('Error parsing ROM JSON:', e);
                          
                          // Fallback for legacy format
                          return (
                            <div className="mobile-file-link">
                              <a 
                                href={`${API_BASE_URL}${game.rom}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="file-button"
                              >
                                默认版本
                              </a>
                              <div className="file-size">{game.romSize || 'N/A'}</div>
                            </div>
                          );
                        }
                        
                        return null;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div className="mobile-card-footer">
                <div className="multiplayer-status mobile-multiplayer">
                  <span className={`multiplayer-badge ${game.multiplayer === 1 ? 'enabled' : 'disabled'}`}>
                    多人游戏: {game.multiplayer === 1 ? '已启用' : '已禁用'}
                  </span>
                </div>
                <div className="mobile-optimized-status mobile-optimized">
                  <span className={`mobile-badge ${game.mobileoptimized === 1 || game.mobileoptimized === undefined ? 'optimized' : 'not-optimized'}`}>
                    移动端: {game.mobileoptimized === 1 || game.mobileoptimized === undefined ? '已优化' : '未优化'}
                  </span>
                </div>
                <div className="emulator-status mobile-emulator">
                  <span className={`emulator-badge ${game.emulator ? 'custom' : 'default'}`}>
                    模拟器: {game.emulator ? `自定义 (${game.emulator})` : '平台默认'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    } else if (type === 'platforms') {
      return (
        <div className="mobile-card-view">
          {items.map(platform => (
            <div className="mobile-card" key={platform.id}>
              <div className="mobile-card-header">
                <span className="mobile-card-id">ID: {platform.id}</span>
                <div className="mobile-card-actions">
                  <button 
                    className="edit-button"
                    onClick={() => navigate(`/updateplatform/${platform.id}`)}
                  >
                    Edit
                  </button>
                  {authClient.isAdmin() && (
                    <button 
                      className="delete-button"
                      onClick={() => handleDelete(platform.id, 'platforms')}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              
              <div className="mobile-card-cover">
                {platform.img && (
                  <div className="mobile-card-image-container">
                    <img 
                      src={`${API_BASE_URL}${platform.img}`} 
                      alt={platform.nameeng || platform.namechn || "Platform Image"} 
                      className="mobile-card-image"
                    />
                    <div className="mobile-card-image-details">
                      <div className="file-size">{platform.imgSize || 'N/A'}</div>
                      <div className={platform.compressed ? 'compressed-badge' : 'not-compressed-badge'}>
                        {platform.compressed ? 'Compressed' : 'Not Compressed'}
                      </div>
                      {!isCompressing && (
                        <button 
                          className="compress-button"
                          onClick={() => handleCompress(platform.id, 'platforms', platform.img, platform.compressed)}
                          disabled={isCompressing}
                        >
                          {platform.compressed ? 'Re-compress' : 'Compress'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mobile-card-content">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">中文名称：</span>
                  <span className="mobile-card-value">{platform.namechn}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">英文名称：</span>
                  <span className="mobile-card-value">{platform.nameeng}</span>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">模拟器：</span>
                  <span className="mobile-card-value">{platform.emulator}</span>
                </div>
                
                {platform.emulatoroptions && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">模拟器选项：</span>
                    <span className="mobile-card-value">{platform.emulatoroptions}</span>
                  </div>
                )}
                
                {platform.description && (
                  <div className="mobile-card-field full-width">
                    <span className="mobile-card-label">描述：</span>
                    <div className="description-container">
                      <div className={`description-text ${expandedDescriptions[`platforms-${platform.id}`] ? 'expanded' : ''}`}>
                        {platform.description}
                      </div>
                      {platform.description && platform.description.length > 100 && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleDescription(platform.id, 'platforms')}
                        >
                          {expandedDescriptions[`platforms-${platform.id}`] ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {platform.style && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">样式：</span>
                    <span className="mobile-card-value">{platform.style}</span>
                  </div>
                )}
                
                {platform.defaultyear && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">默认年份：</span>
                    <span className="mobile-card-value">{platform.defaultyear}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    } else if (type === 'feedback') {
      return (
        <div className="mobile-card-view">
          {items.map(item => (
            <div className={`mobile-card feedback-card feedback-${item.status}`} key={item.id}>
              <div className="mobile-card-header">
                <div className="mobile-card-metadata">
                  <span className="mobile-card-id">ID: {item.id}</span>
                  <span className="feedback-type-badge mobile-badge">{getFeedbackTypeDisplay(item.type)}</span>
                  <span className={`status-badge ${getStatusBadgeClass(item.status)} mobile-badge`}>
                    {getFeedbackStatusDisplay(item.status)}
                  </span>
                </div>
              </div>
              
              <div className="mobile-card-content">
                <div className="mobile-card-field">
                  <span className="mobile-card-label">标题：</span>
                  <span className="mobile-card-value mobile-feedback-title">{item.title}</span>
                </div>
                
                {item.game_id && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">游戏：</span>
                    <Link to={`/game-details/${item.game_id}`} className="feedback-game-link">
                      {item.game_name || `ID: ${item.game_id}`}
                    </Link>
                  </div>
                )}
                
                <div className="mobile-card-field full-width">
                  <span className="mobile-card-label">内容：</span>
                  <div className="description-container">
                    <div className={`description-text ${expandedDescriptions[`feedback-${item.id}`] ? 'expanded' : ''}`}>
                      {item.content}
                    </div>
                    {item.content && item.content.length > 100 && (
                      <button 
                        className="expand-button"
                        onClick={() => toggleDescription(item.id, 'feedback')}
                      >
                        {expandedDescriptions[`feedback-${item.id}`] ? '收起' : '展开'}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">用户：</span>
                  <span className="mobile-card-value">{item.name || '匿名'}</span>
                </div>
                
                {item.email && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">邮箱：</span>
                    <span className="mobile-card-value">{item.email}</span>
                  </div>
                )}
                
                <div className="mobile-card-field">
                  <span className="mobile-card-label">日期：</span>
                  <span className="mobile-card-value">{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
                
                {item.file_path && (
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">附件：</span>
                    <a 
                      href={`${API_BASE_URL}${item.file_path}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="file-button"
                    >
                      下载附件
                    </a>
                  </div>
                )}
              </div>
              
              <div className="mobile-card-actions feedback-actions">
                {item.status !== 'resolved' && (
                  <button 
                    className="feedback-resolve-button"
                    onClick={() => handleUpdateFeedbackStatus(item.id, 'resolved')}
                  >
                    标记为已解决
                  </button>
                )}
                {item.status !== 'rejected' && (
                  <button 
                    className="feedback-reject-button"
                    onClick={() => handleUpdateFeedbackStatus(item.id, 'rejected')}
                  >
                    拒绝
                  </button>
                )}
                {item.status !== 'pending' && (
                  <button 
                    className="feedback-pending-button"
                    onClick={() => handleUpdateFeedbackStatus(item.id, 'pending')}
                  >
                    重置为待处理
                  </button>
                )}
                <button 
                  className="delete-button"
                  onClick={() => handleDelete(item.id, 'feedback')}
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  // Handle logout
  const handleLogout = () => {
    authClient.logout();
    navigate('/login');
  };

  const unhandledGameApplications = gameApplications.filter(
    (application) => !application.status || application.status === 'pending'
  );

  const formatIpLocation = (location, ipAddress) => {
    const formattedLocation = [location?.country, location?.regionName, location?.city, location?.district]
      .filter(Boolean)
      .filter((part, index, parts) => parts.indexOf(part) === index)
      .join(' · ');
    return formattedLocation || ipAddress || '未知位置';
  };

  const handleCopyUnhandledGameApplications = async () => {
    if (unhandledGameApplications.length === 0) {
      alert('没有待处理的游戏申请。');
      return;
    }

    const formattedApplications = unhandledGameApplications
      .map((application, index) => {
        const chineseTitle = application.title_cn?.trim();
        const englishTitle = application.title_en?.trim();
        const title = chineseTitle && englishTitle
          ? `${chineseTitle} (${englishTitle})`
          : chineseTitle || englishTitle || '未命名游戏';
        const platform = application.platform?.trim() || '平台未填写';

        return `${index + 1}. ${title} - ${platform}`;
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(formattedApplications);
      alert(`已复制 ${unhandledGameApplications.length} 个待处理的游戏申请。`);
    } catch (error) {
      console.error('Error copying game applications:', error);
      alert('复制失败，请检查浏览器的剪贴板权限。');
    }
  };

  // If not authenticated, don't render anything (redirect happens in useEffect)
  if (!isAuthenticated) {
    return null;
  }
  
  // Show loading state while fetching data
  if (isLoading && retryCount === 0) {
    return (
      <div className="admin-loading-container">
        <h2>正在加载数据...</h2>
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  // Show error state if failed to load data after retries
  if (loadError && retryCount >= 3) {
    return (
      <div className="admin-error-container">
        <h2>加载数据失败</h2>
        <p>{loadError}</p>
        <button 
          className="admin-retry-button"
          onClick={() => {
            setRetryCount(0); // 重置重试计数以触发新的获取
          }}
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="manage-container">
      <div className="manage-header">
        <div className="manage-header-top">
          <h1>管理面板</h1>
          <div className="user-info">
            <span>{authClient.getCurrentUser()?.displayName || '管理员'}</span>
            <button onClick={handleLogout} className="logout-button">退出登录</button>
          </div>
        </div>
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'games' ? 'active' : ''}`}
            onClick={() => setActiveTab('games')}
          >
            游戏
            {pendingVisitorGames.length > 0 && (
              <span className="pending-game-count" aria-label={`${pendingVisitorGames.length} 个待审核访客游戏`}>
                {pendingVisitorGames.length}
              </span>
            )}
          </button>
          <button 
            className={`tab-button ${activeTab === 'platforms' ? 'active' : ''}`}
            onClick={() => setActiveTab('platforms')}
          >
            平台
          </button>
          <button 
            className={`tab-button ${activeTab === 'docs' ? 'active' : ''}`}
            onClick={() => setActiveTab('docs')}
          >
            文档
          </button>
          <button
            className={`tab-button ${activeTab === 'oral_history' ? 'active' : ''}`}
            onClick={() => setActiveTab('oral_history')}
          >
            口述史
          </button>
          <button 
            className={`tab-button ${activeTab === 'game_applications' ? 'active' : ''}`}
            onClick={() => setActiveTab('game_applications')}
          >
            游戏申请
          </button>
          <button
            className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback')}
          >
            反馈
          </button>
          {authClient.isAdmin() && (
            <>
              <button 
                className={`tab-button ${activeTab === 'version' ? 'active' : ''}`}
                onClick={() => setActiveTab('version')}
              >
                版本历史
              </button>
              <button 
                className={`tab-button ${activeTab === 'onboarding' ? 'active' : ''}`}
                onClick={() => setActiveTab('onboarding')}
              >
                游戏推荐
              </button>
              <button 
                className={`tab-button ${activeTab === 'applications' ? 'active' : ''}`}
                onClick={() => setActiveTab('applications')}
              >
                志愿者申请
              </button>
              <button 
                className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                用户管理
              </button>
            </>
          )}
        </div>
        {activeTab === 'games' ? (
          <div className="header-actions">
            <Link to="/add" className="add-button">添加新游戏</Link>
          </div>
        ) : activeTab === 'platforms' && authClient.isAdmin() ? (
          <div className="header-actions">
            <Link to="/addplatform" className="add-button">添加新平台</Link>
          </div>
        ) : (
          <div className="header-actions">
            {/* Empty placeholder to maintain grid layout */}
          </div>
        )}
      </div>

      {/* Warning message if data had errors but we have some data to show */}
      {loadError && (retryCount >= 3) && (
        <div className="admin-warning-message">
          <p>警告：由于连接问题，部分数据可能丢失。{loadError}</p>
        </div>
      )}

      {/* Only show search bar for games, platforms, and feedback tabs */}
      {activeTab !== 'onboarding' && activeTab !== 'version' && activeTab !== 'docs' && activeTab !== 'oral_history' && (
        <div className="manage-search-bar">
          <input
            type="text"
            placeholder={`搜索${activeTab === 'games' ? '游戏' : activeTab === 'platforms' ? '平台' : '反馈'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Pagination info - only show for games and platforms tabs */}
      {(activeTab === 'games' || activeTab === 'platforms') && (
        <div className="pagination-info">
          {activeTab === 'games' && (
            <p>
              显示 {filteredGames.length > 0 ? indexOfFirstItem + 1 : 0} - {Math.min(indexOfLastItem, filteredGames.length)} 条，共 {filteredGames.length} 个游戏
            </p>
          )}
          {activeTab === 'platforms' && (
            <p>
              显示 {filteredPlatforms.length > 0 ? indexOfFirstItem + 1 : 0} - {Math.min(indexOfLastItem, filteredPlatforms.length)} 条，共 {filteredPlatforms.length} 个平台
            </p>
          )}
        </div>
      )}

      {activeTab === 'games' && (
        <>
        {pendingVisitorGames.length > 0 && (
          <section className="pending-visitor-games" aria-labelledby="pending-visitor-games-title">
            <h2 id="pending-visitor-games-title">待审核访客游戏 ({pendingVisitorGames.length})</h2>
            <div className="table-wrapper">
              <table className="manage-table">
                <thead><tr><th>游戏</th><th>平台</th><th>贡献者</th><th>提交 IP</th><th>提交时间</th><th>审核</th></tr></thead>
                <tbody>
                  {pendingVisitorGames.map((submission) => (
                    <tr key={submission.id} className="visitor-submission-row">
                      <td>{submission.titlechn || submission.titleeng || '未命名游戏'}</td>
                      <td>{submission.platform_namechn || submission.platform_nameeng || submission.platform}</td>
                      <td>{submission.contributor_alias}</td>
                      <td><code>{submission.submitter_ip}</code></td>
                      <td>{new Date(submission.submitted_at).toLocaleString()}</td>
                      <td>
                        {submission.game_application_id && <div className="linked-request-label">关联申请 #{submission.game_application_id}</div>}
                        <Link className="edit-button pending-preview-link" to={`/manage/pending-game/${submission.id}`}>打开正式页面预览</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <div className="table-wrapper">
          <div className="filter-controls">
            <label className="filter-checkbox">
              <input 
                type="checkbox" 
                checked={showOnlyMyGames} 
                onChange={() => setShowOnlyMyGames(!showOnlyMyGames)} 
              />
              <span className="checkmark"></span>
              只显示我创建的游戏
            </label>
          </div>
          <table className="manage-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>封面</th>
                <th>英文标题</th>
                <th>中文标题</th>
                <th>平台</th>
                <th>年份</th>
                <th>开发商</th>
                <th>发行商</th>
                <th>翻译者</th>
                <th>创建者</th>
                <th>浏览量</th>
                <th>描述</th>
                <th>额外提示</th>
                <th>手册</th>
                <th>ROM文件</th>
                <th>加载名称</th>
                <th>启动选项</th>
                <th>多人游戏</th>
                <th>移动端优化</th>
                <th>模拟器</th>
                <th>标签</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {currentGames.map(game => (
                <tr key={game.id}>
                  <td>{game.id}</td>
                  <td>
                    {game.cover && (
                      <div className="file-with-size">
                        <img 
                          src={`${API_BASE_URL}${game.cover}`} 
                          alt={game.titleeng || game.titlechn}
                          className="preview-image"
                        />
                        <div className="file-size">{game.coverSize || 'N/A'}</div>
                        <div className={game.compressed ? 'compressed-badge' : 'not-compressed-badge'}>
                          {game.compressed ? '已压缩' : '未压缩'}
                        </div>
                        {!isCompressing && (
                          <button 
                            className="compress-button"
                            onClick={() => handleCompress(game.id, 'games', game.cover, game.compressed)}
                            disabled={isCompressing}
                          >
                            {game.compressed ? '重新压缩' : '压缩'}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{game.titleeng}</td>
                  <td>{game.titlechn}</td>
                  <td>
                    {(() => {
                      const platform = getPlatformDetails(game.platform);
                      return platform.namechn && platform.nameeng 
                        ? `${platform.namechn} (${platform.nameeng})`
                        : platform.namechn || platform.nameeng || `ID: ${game.platform}`;
                    })()}
                  </td>
                  <td>{game.year}</td>
                  <td>{game.dev}</td>
                  <td>{game.publisher}</td>
                  <td>{game.translator}</td>
                  <td>
                    {game.adminName || (game.admin && adminUsers[game.admin]) || '-'}
                  </td>
                  <td>
                    <div className="view-count">
                      {game.views || 0}
                    </div>
                  </td>
                  <td>
                    <div className="description-container">
                      <div className={`description-text ${expandedDescriptions[`games-${game.id}`] ? 'expanded' : ''}`}>
                        {game.desc}
                      </div>
                      {game.desc && game.desc.length > 100 && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleDescription(game.id, 'games')}
                        >
                          {expandedDescriptions[`games-${game.id}`] ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="description-container">
                      <div className={`description-text ${expandedDescriptions[`games-hint-${game.id}`] ? 'expanded' : ''}`}>
                        {game.additional_hint || '-'}
                      </div>
                      {game.additional_hint && game.additional_hint.length > 100 && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleDescription(game.id, 'games-hint')}
                        >
                          {expandedDescriptions[`games-hint-${game.id}`] ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    {game.manual && (
                      <div className="file-with-size">
                        {(() => {
                          try {
                            const manuals = JSON.parse(game.manual);
                            
                            if (Array.isArray(manuals) && manuals.length > 0) {
                              return (
                                <>
                                  {manuals.map((manual, index) => (
                                    <div key={index} style={{ marginBottom: '10px' }}>
                                      <a 
                                        href={`${API_BASE_URL}${manual.path}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="file-button"
                                      >
                                        {manual.name || `手册 ${index + 1}`}
                                      </a>
                                      <div className="file-size">{game.manualSizes?.[index] || 'N/A'}</div>
                                    </div>
                                  ))}
                                </>
                              );
                            }
                          } catch (e) {
                            console.error('Error parsing manual JSON:', e);
                          }
                          
                          return null;
                        })()}
                      </div>
                    )}
                  </td>
                  <td>
                    {game.rom && (
                      <div className="file-with-size">
                        {(() => {
                          try {
                            const roms = JSON.parse(game.rom);
                            
                            if (Array.isArray(roms) && roms.length > 0) {
                              return (
                                <>
                                  {roms.map((rom, index) => (
                                    <div key={index} style={{ marginBottom: '10px' }}>
                                      <a 
                                        href={`${API_BASE_URL}${rom.path}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="file-button"
                                      >
                                        {rom.type}
                                      </a>
                                      <div className="file-size">{game.romSizes?.[index] || 'N/A'}</div>
                                    </div>
                                  ))}
                                </>
                              );
                            }
                          } catch (e) {
                            console.error('Error parsing ROM JSON:', e);
                            
                            // Fallback for legacy format
                            return (
                              <div className="file-with-size">
                                <a 
                                  href={`${API_BASE_URL}${game.rom}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="file-button"
                                >
                                  默认版本
                                </a>
                                <div className="file-size">{game.romSize || 'N/A'}</div>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                    )}
                  </td>
                  <td>
                    {game.loadasname && (
                      <span className="loadasname-text">{game.loadasname}</span>
                    )}
                  </td>
                  <td>
                    {game.launchoptions && (
                      <span className="launchoptions-text">{game.launchoptions}</span>
                    )}
                  </td>
                  <td>
                    <div className="multiplayer-status">
                      {game.multiplayer === 1 ? (
                        <span className="multiplayer-badge enabled">已启用</span>
                      ) : (
                        <span className="multiplayer-badge disabled">已禁用</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="mobile-optimized-status">
                      {game.mobileoptimized === 1 || game.mobileoptimized === undefined ? (
                        <span className="mobile-badge optimized">已优化</span>
                      ) : (
                        <span className="mobile-badge not-optimized">未优化</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="emulator-status">
                      {game.emulator ? (
                        <span className="emulator-badge custom">自定义: {game.emulator}</span>
                      ) : (
                        <span className="emulator-badge default">平台默认</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="game-tags-container">
                      {gameTags[game.id]?.map(tag => (
                        <span 
                          key={tag.id} 
                          className="game-tag"
                          style={{ backgroundColor: tag.color || '#6c757d' }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-button"
                        onClick={() => navigate(`/update/${game.id}`)}
                      >
                        编辑
                      </button>
                      {(authClient.isAdmin() || game.admin === currentUser?.id) && (
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(game.id, 'games')}
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View for Games */}
          <MobileCardView items={currentGames} type="games" />

          {/* Pagination component for games */}
          <Pagination totalItems={filteredGames.length} />
        </div>
        </>
      )}
      {activeTab === 'platforms' && (
        <div className="table-wrapper">
          <table className="manage-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>图片</th>
                <th>中文名称</th>
                <th>英文名称</th>
                <th>模拟器</th>
                <th>模拟器选项</th>
                <th>描述</th>
                <th>样式</th>
                <th>默认年份</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {currentPlatforms.map(platform => (
                <tr key={platform.id}>
                  <td>{platform.id}</td>
                  <td>
                    {platform.img && (
                      <div className="file-with-size">
                        <img 
                          src={`${API_BASE_URL}${platform.img}`} 
                          alt={platform.nameeng || platform.namechn || "Platform Image"} 
                          className="preview-image"
                        />
                        <div className="file-size">{platform.imgSize || 'N/A'}</div>
                        <div className={platform.compressed ? 'compressed-badge' : 'not-compressed-badge'}>
                          {platform.compressed ? '已压缩' : '未压缩'}
                        </div>
                        {!isCompressing && (
                          <button 
                            className="compress-button"
                            onClick={() => handleCompress(platform.id, 'platforms', platform.img, platform.compressed)}
                            disabled={isCompressing}
                          >
                            {platform.compressed ? '重新压缩' : '压缩'}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{platform.namechn}</td>
                  <td>{platform.nameeng}</td>
                  <td>{platform.emulator}</td>
                  <td>{platform.emulatoroptions}</td>
                  <td>
                    <div className="description-container">
                      <div className={`description-text ${expandedDescriptions[`platforms-${platform.id}`] ? 'expanded' : ''}`}>
                        {platform.description}
                      </div>
                      {platform.description && platform.description.length > 100 && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleDescription(platform.id, 'platforms')}
                        >
                          {expandedDescriptions[`platforms-${platform.id}`] ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{platform.style || '-'}</td>
                  <td>{platform.defaultyear || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-button"
                        onClick={() => navigate(`/updateplatform/${platform.id}`)}
                      >
                        编辑
                      </button>
                      {authClient.isAdmin() && (
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(platform.id, 'platforms')}
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View for Platforms */}
          <MobileCardView items={currentPlatforms} type="platforms" />

          {/* Pagination component for platforms */}
          <Pagination totalItems={filteredPlatforms.length} />
        </div>
      )}
      {activeTab === 'feedback' && (
        <FeedbackManagement searchTerm={searchTerm} />
      )}

      {activeTab === 'oral_history' && (
        <OralHistoryManagement games={games} platforms={platforms} />
      )}
      
      {activeTab === 'version' && (
        <div className="version-container">
          <div className="version-management-section">
            <div className="todo-section">
              <h2>待办事项</h2>
              <div className="add-todo-form">
                <input
                  type="text"
                  placeholder="添加新的待办事项..."
                  value={newTodoItem}
                  onChange={(e) => setNewTodoItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
                  className="todo-input"
                />
                <button 
                  className="add-todo-button"
                  onClick={handleAddTodo}
                >
                  添加
                </button>
              </div>
              
              <div className="todo-list">
                {todoList.length === 0 ? (
                  <p className="no-data-message">暂无待办事项</p>
                ) : (
                  <ul>
                    {todoList.map(item => (
                      <li key={item.id} className={`todo-item todo-${item.status}`}>
                        <span className="todo-text">{item.task}</span>
                        <div className="todo-meta">
                          <span className="todo-date">{new Date(item.created_at).toLocaleDateString()}</span>
                          <div className="todo-actions">
                            {item.status === 'pending' && (
                              <>
                                <button 
                                  onClick={() => handleUpdateTodoStatus(item.id, 'completed')}
                                  className="todo-complete-button"
                                >
                                  完成
                                </button>
                                <button 
                                  onClick={() => handleUpdateTodoStatus(item.id, 'delayed')}
                                  className="todo-delay-button"
                                >
                                  延迟
                                </button>
                                <button 
                                  onClick={() => handleUpdateTodoStatus(item.id, 'removed')}
                                  className="todo-remove-button"
                                >
                                  删除
                                </button>
                              </>
                            )}
                            {item.status !== 'pending' && (
                              <button 
                                onClick={() => handleUpdateTodoStatus(item.id, 'pending')}
                                className="todo-reset-button"
                              >
                                重置
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            
            <div className="version-history-section">
              <h2>版本历史</h2>
              <div className="add-version-form">
                <div className="change-items-container">
                  <h3>添加变更</h3>
                  <div className="add-change-form">
                    <input
                      type="text"
                      placeholder="添加更新项..."
                      value={newChangeItem}
                      onChange={(e) => setNewChangeItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddChangeItem()}
                      className="change-input"
                    />
                    <button 
                      className="add-change-button"
                      onClick={handleAddChangeItem}
                    >
                      添加
                    </button>
                  </div>
                  
                  {changeItems.length > 0 && (
                    <div className="change-items-list">
                      <h4>当前变更：</h4>
                      <ul>
                        {changeItems.map((item, index) => (
                          <li key={index} className="change-item">
                            <span>{item}</span>
                            <button 
                              onClick={() => handleRemoveChangeItem(index)}
                              className="remove-change-button"
                            >
                              移除
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button 
                        className="submit-version-button"
                        onClick={handleSubmitVersion}
                      >
                        提交版本更新
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="version-history-list">
                  <h3>历史记录</h3>
                  {versionHistory.length === 0 ? (
                    <p className="no-data-message">暂无版本历史记录</p>
                  ) : (
                    <div className="version-timeline">
                      {versionHistory.map(version => (
                        <div key={version.id} className="version-item">
                          <div className="version-header">
                            <span className="version-date">{new Date(version.update_time).toLocaleString()}</span>
                          </div>
                          <ul className="version-changes">
                            {version.changes.map((change, index) => (
                              <li key={index} className="version-change-item">{change}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'onboarding' && (
        <div className="onboarding-container">
          <div className="onboarding-header">
            <h2>新用户游戏推荐队列</h2>
            <p className="onboarding-description">
              这些游戏将在新用户首次访问Feed页面时，按顺序添加到他们的推荐队列最前面。
              <br />
              <strong>注意：</strong>在移动设备上，未优化移动端的游戏会被自动过滤。
            </p>
          </div>
          
          <div className="onboarding-add-section">
            <h3>添加游戏到推荐队列</h3>
            <div className="onboarding-add-form">
              <input
                type="text"
                placeholder="搜索游戏 (ID、英文标题、中文标题)..."
                value={searchGameTerm}
                onChange={(e) => setSearchGameTerm(e.target.value)}
                className="game-search-input"
              />
              <select
                value={selectedGameId}
                onChange={(e) => setSelectedGameId(e.target.value)}
                className="game-select"
              >
                <option value="">-- 选择游戏 --</option>
                {filteredAvailableGames.slice(0, 50).map(game => (
                  <option key={game.id} value={game.id}>
                    ID:{game.id} - {game.titlechn || game.titleeng} ({game.mobileoptimized ? '移动端优化' : '仅PC'})
                  </option>
                ))}
              </select>
              <button 
                className="add-queue-button"
                onClick={handleAddToQueue}
                disabled={!selectedGameId}
              >
                添加到队列
              </button>
            </div>
          </div>
          
          <div className="onboarding-queue-section">
            <h3>当前推荐队列 ({onboardingQueue.length} 个游戏)</h3>
            {onboardingQueue.length === 0 ? (
              <p className="no-data-message">推荐队列为空。添加游戏以创建新用户的初始推荐。</p>
            ) : (
              <div className="queue-list">
                {onboardingQueue.map((item, index) => (
                  <div key={item.id} className="queue-item">
                    <div className="queue-item-position">#{index + 1}</div>
                    <div className="queue-item-cover">
                      {item.cover && (
                        <img 
                          src={`${API_BASE_URL}${item.cover}`} 
                          alt={item.titlechn || item.titleeng}
                        />
                      )}
                    </div>
                    <div className="queue-item-info">
                      <div className="queue-item-title">
                        <strong>{item.titlechn || item.titleeng}</strong>
                        {item.titlechn && item.titleeng && (
                          <span className="queue-item-subtitle">{item.titleeng}</span>
                        )}
                      </div>
                      <div className="queue-item-meta">
                        <span className="queue-item-id">ID: {item.game_id}</span>
                        <span className="queue-item-platform">
                          {item.platform_namechn || item.platform_nameeng}
                        </span>
                        <span className={`queue-item-mobile ${item.mobileoptimized ? 'optimized' : 'not-optimized'}`}>
                          {item.mobileoptimized ? '✓ 移动端优化' : '✗ 仅PC'}
                        </span>
                      </div>
                    </div>
                    <div className="queue-item-actions">
                      <button 
                        className="queue-move-button"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        title="上移"
                      >
                        ↑
                      </button>
                      <button 
                        className="queue-move-button"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === onboardingQueue.length - 1}
                        title="下移"
                      >
                        ↓
                      </button>
                      <button 
                        className="queue-remove-button"
                        onClick={() => handleRemoveFromQueue(item.id)}
                        title="移除"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'game_applications' && (
        <div className="applications-container">
          <div className="applications-heading">
            <h2>游戏申请 ({gameApplications.length})</h2>
            <button
              type="button"
              className="copy-pending-applications-button"
              onClick={handleCopyUnhandledGameApplications}
              disabled={unhandledGameApplications.length === 0}
            >
              复制待处理游戏 ({unhandledGameApplications.length})
            </button>
          </div>
          {gameApplications.length === 0 ? (
            <p className="no-data-message">暂无游戏申请。</p>
          ) : (
            <div className="table-wrapper">
              <table className="manage-table applications-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>封面</th>
                    <th>中文名</th>
                    <th>英文名</th>
                    <th>平台</th>
                    <th>可模拟</th>
                    <th>已收录</th>
                    <th>状态</th>
                    <th>IP 位置</th>
                    <th>提交时间</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {gameApplications.map((app) => (
                    <React.Fragment key={app.id}>
                      <tr className={expandedGameApps[app.id] ? 'expanded-row' : ''}>
                        <td>{app.id}</td>
                        <td>
                          {app.poster && app.poster !== '未找到封面' && (
                            <img 
                              src={app.poster} 
                              alt={app.title_cn || app.title_en}
                              className="preview-image"
                              style={{ maxWidth: '60px', maxHeight: '80px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </td>
                        <td>{app.title_cn || '-'}</td>
                        <td>{app.title_en || '-'}</td>
                        <td>{app.platform || '-'}</td>
                        <td>
                          <span className={`status-badge ${
                            app.is_emulatable === 'yes' ? 'status-resolved' : 
                            app.is_emulatable === 'maybe' ? 'status-pending' : 'status-rejected'
                          }`}>
                            {app.is_emulatable === 'yes' ? '是' : app.is_emulatable === 'maybe' ? '可能' : '否'}
                          </span>
                        </td>
                        <td>
                          {app.is_in_library ? (
                            <span className="status-badge status-resolved">
                              {app.library_game_id ? (
                                <a href={`https://rgm.games/game/${app.library_game_id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                  已收录 #{app.library_game_id}
                                </a>
                              ) : '已收录'}
                            </span>
                          ) : (
                            <span className="status-badge status-rejected">未收录</span>
                          )}
                        </td>
                        <td>
                          <select
                            value={app.status || 'pending'}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              try {
                                await apiClient.put(`/game-applications/${app.id}/status`, { 
                                  status: newStatus, 
                                  admin_notes: app.admin_notes || null,
                                  added_url: newStatus === 'added' ? (app.added_url || '') : null 
                                });
                                setGameApplications(gameApplications.map(a => 
                                  a.id === app.id ? { ...a, status: newStatus, reviewed_at: new Date().toISOString() } : a
                                ));
                              } catch (error) {
                                console.error('Error updating game application status:', error);
                                alert('更新状态失败');
                              }
                            }}
                            className={`role-select ${
                              app.status === 'added' ? 'admin-role' : 
                              app.status === 'ignored' ? 'user-role' : ''
                            }`}
                          >
                            <option value="pending">待处理</option>
                            <option value="ignored">暂不处理</option>
                            <option value="added">已添加</option>
                          </select>
                          {(app.status === 'added' || app.status === 'ignored') && (
                            <div style={{ marginTop: '4px' }}>
                              <input
                                type="text"
                                placeholder="备注（用户可见）"
                                value={app.admin_notes || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newNote = e.target.value;
                                  setGameApplications(gameApplications.map(a => 
                                    a.id === app.id ? { ...a, admin_notes: newNote } : a
                                  ));
                                }}
                                onBlur={async (e) => {
                                  const newNote = e.target.value;
                                  try {
                                    await apiClient.put(`/game-applications/${app.id}/status`, { 
                                      status: app.status, 
                                      admin_notes: newNote || null,
                                      added_url: app.added_url || null 
                                    });
                                  } catch (error) {
                                    console.error('Error saving admin_notes:', error);
                                    alert('保存备注失败');
                                  }
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                style={{ 
                                  width: '100%', 
                                  padding: '4px 6px', 
                                  fontSize: '0.75rem', 
                                  background: app.status === 'added' ? 'rgba(40,167,69,0.1)' : 'rgba(255,193,7,0.1)', 
                                  border: `1px solid ${app.status === 'added' ? 'rgba(40,167,69,0.3)' : 'rgba(255,193,7,0.3)'}`, 
                                  borderRadius: '4px', 
                                  color: app.status === 'added' ? '#28a745' : '#ffc107' 
                                }}
                              />
                            </div>
                          )}
                          {app.status === 'added' && (
                            <div style={{ marginTop: '4px' }}>
                              <input
                                type="text"
                                placeholder="游戏页面URL"
                                value={app.added_url || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newUrl = e.target.value;
                                  setGameApplications(gameApplications.map(a => 
                                    a.id === app.id ? { ...a, added_url: newUrl } : a
                                  ));
                                }}
                                onBlur={async (e) => {
                                  const newUrl = e.target.value;
                                  try {
                                    await apiClient.put(`/game-applications/${app.id}/status`, { 
                                      status: 'added', 
                                      admin_notes: app.admin_notes || null,
                                      added_url: newUrl || null 
                                    });
                                  } catch (error) {
                                    console.error('Error saving added_url:', error);
                                    alert('保存URL失败');
                                  }
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                style={{ 
                                  width: '100%', 
                                  padding: '4px 6px', 
                                  fontSize: '0.75rem', 
                                  background: 'rgba(40,167,69,0.1)', 
                                  border: '1px solid rgba(40,167,69,0.3)', 
                                  borderRadius: '4px', 
                                  color: '#28a745' 
                                }}
                              />
                            </div>
                          )}
                        </td>
                        <td title={app.ip_address || ''}>
                          {app.ip_address ? formatIpLocation(app.location, app.ip_address) : '-'}
                        </td>
                        <td>{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString('zh-CN') : '-'}</td>
                        <td>
                          <div className="application-row-actions">
                            {authClient.isAdmin() && (
                              <a
                                className="create-from-application-btn"
                                href={`/add?fromApplication=${encodeURIComponent(app.id)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                一件创建
                              </a>
                            )}
                            <button
                              className="view-details-btn"
                              onClick={() => {
                                setExpandedGameApps({
                                  ...expandedGameApps,
                                  [app.id]: !expandedGameApps[app.id]
                                });
                              }}
                            >
                              {expandedGameApps[app.id] ? '收起' : '详情'}
                            </button>
                            <button
                              className="delete-btn"
                              onClick={async () => {
                                if (window.confirm('确定要删除这个游戏申请吗？')) {
                                  try {
                                    await apiClient.delete(`/game-applications/${app.id}`);
                                    setGameApplications(gameApplications.filter(a => a.id !== app.id));
                                  } catch (error) {
                                    console.error('Error deleting game application:', error);
                                    alert('删除失败');
                                  }
                                }
                              }}
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                      {allVisitorGameSubmissions.some((submission) => Number(submission.game_application_id) === Number(app.id)) && (
                        <tr className="linked-submissions-row">
                          <td colSpan="11">
                            <strong>关联的游戏提交</strong>
                            <div className="linked-submissions-list">
                              {allVisitorGameSubmissions
                                .filter((submission) => Number(submission.game_application_id) === Number(app.id))
                                .map((submission) => (
                                  <div key={submission.id} className={`linked-submission-item ${submission.status}`}>
                                    <span>提交 #{submission.id}</span>
                                    <span>{submission.titlechn || submission.titleeng || '未命名游戏'}</span>
                                    <span>{submission.contributor_alias}</span>
                                    <span>{submission.status === 'pending' ? '待审核' : submission.status === 'approved' ? '已通过' : '未通过'}</span>
                                    {submission.status === 'pending'
                                      ? <Link to={`/manage/pending-game/${submission.id}`}>预览并审核</Link>
                                      : submission.review_comment && <span>留言：{submission.review_comment}</span>}
                                  </div>
                                ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      {expandedGameApps[app.id] && (
                        <tr className="details-row">
                          <td colSpan="11">
                            <div className="application-details">
                              <div className="detail-section">
                                <h4>游戏信息</h4>
                                <p><strong>中文名：</strong>{app.title_cn || '未填写'}</p>
                                <p><strong>英文名：</strong>{app.title_en || '未填写'}</p>
                                {app.title_jp && <p><strong>日文名：</strong>{app.title_jp}</p>}
                                <p><strong>平台：</strong>{app.platform || '未填写'}</p>
                                <p><strong>开发商：</strong>{app.developer || '未填写'}</p>
                                <p><strong>发行商：</strong>{app.publisher || '未填写'}</p>
                                <p><strong>发行日期：</strong>{app.release_date || '未填写'}</p>
                                <p><strong>可模拟：</strong>{app.is_emulatable === 'yes' ? '是' : app.is_emulatable === 'maybe' ? '可能' : '否'}</p>
                              </div>
                              {app.description && (
                                <div className="detail-section">
                                  <h4>游戏简介</h4>
                                  <p>{app.description}</p>
                                </div>
                              )}
                              <div className="detail-section">
                                <h4>搜索信息</h4>
                                <p><strong>搜索方式：</strong>{app.search_mode === 'name' ? '按名称搜索' : app.search_mode === 'description' ? '按描述搜索' : app.search_mode || '未知'}</p>
                                <p><strong>搜索内容：</strong>{app.search_query || '未填写'}</p>
                              </div>
                              {app.screenshots && (
                                <div className="detail-section">
                                  <h4>游戏截图</h4>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {(() => {
                                      try {
                                        const screenshots = typeof app.screenshots === 'string' ? JSON.parse(app.screenshots) : app.screenshots;
                                        if (Array.isArray(screenshots)) {
                                          return screenshots.filter(s => s && s !== '未找到截图').map((screenshot, idx) => (
                                            <img 
                                              key={idx}
                                              src={screenshot} 
                                              alt={`Screenshot ${idx + 1}`}
                                              style={{ maxHeight: '100px', borderRadius: '4px', border: '1px solid rgba(228,203,134,0.3)' }}
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                          ));
                                        }
                                      } catch (e) {}
                                      return null;
                                    })()}
                                  </div>
                                </div>
                              )}
                              {app.admin_notes && (
                                <div className="detail-section">
                                  <h4>管理员备注</h4>
                                  <p>{app.admin_notes}</p>
                                </div>
                              )}
                              {app.reviewed_at && (
                                <div className="detail-section">
                                  <h4>审核时间</h4>
                                  <p>{new Date(app.reviewed_at).toLocaleString('zh-CN')}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="applications-container">
          <h2>志愿者申请 ({applications.length})</h2>
          {applications.length === 0 ? (
            <p className="no-data-message">暂无志愿者申请。</p>
          ) : (
            <div className="table-wrapper">
              <table className="manage-table applications-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>姓名</th>
                    <th>邮箱</th>
                    <th>微信</th>
                    <th>状态</th>
                    <th>年龄</th>
                    <th>每周时间</th>
                    <th>提交时间</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <React.Fragment key={app.id}>
                      <tr className={expandedApplications[app.id] ? 'expanded-row' : ''}>
                        <td>{app.id}</td>
                        <td>{app.name}</td>
                        <td>{app.email}</td>
                        <td>{app.wechat || '-'}</td>
                        <td>
                          {app.status === 'student' ? '学生' : 
                           app.status === 'working' ? '在职' : 
                           app.status_description || '其他'}
                        </td>
                        <td>{app.age}</td>
                        <td>{app.hours_per_week}小时</td>
                        <td>{new Date(app.submitted_at).toLocaleDateString('zh-CN')}</td>
                        <td>
                          <button
                            className="view-details-btn"
                            onClick={() => {
                              setExpandedApplications({
                                ...expandedApplications,
                                [app.id]: !expandedApplications[app.id]
                              });
                            }}
                          >
                            {expandedApplications[app.id] ? '收起' : '详情'}
                          </button>
                          <button
                            className="delete-btn"
                            onClick={async () => {
                              if (window.confirm('确定要删除这个申请吗？')) {
                                try {
                                  await apiClient.delete(`/volunteer-applications/${app.id}`);
                                  setApplications(applications.filter(a => a.id !== app.id));
                                } catch (error) {
                                  console.error('Error deleting application:', error);
                                  alert('删除失败');
                                }
                              }
                            }}
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                      {expandedApplications[app.id] && (
                        <tr className="details-row">
                          <td colSpan="9">
                            <div className="application-details">
                              <div className="detail-section">
                                <h4>基本信息</h4>
                                <p><strong>所在地：</strong>{app.workplace || '未填写'}</p>
                              </div>
                              <div className="detail-section">
                                <h4>游戏回忆</h4>
                                <p>{app.game_memories}</p>
                              </div>
                              <div className="detail-section">
                                <h4>加入动机</h4>
                                <p>{app.motivation}</p>
                              </div>
                              {app.additional_info && (
                                <div className="detail-section">
                                  <h4>其他信息</h4>
                                  <p>{app.additional_info}</p>
                                </div>
                              )}
                              <div className="detail-section">
                                <h4>技能水平</h4>
                                <div className="skills-grid">
                                  <div><strong>图片编辑：</strong>{app.photoshop_skill}/4</div>
                                  <div><strong>英语：</strong>{app.english_skill}/4</div>
                                  <div><strong>日语：</strong>{app.japanese_skill}/4</div>
                                  <div><strong>英文网络：</strong>{app.english_internet_habit}/4</div>
                                  <div><strong>模拟器：</strong>{app.emulator_experience}/4</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'users' && authClient.getCurrentUser()?.isAdmin && (
        <UserManagement
          users={users}
          onAddUser={handleAddUser}
          onUpdateUser={handleUpdateUser}
          onDeleteUser={handleDeleteUser}
          onResetPassword={handleResetPassword}
          currentUserId={authClient.getCurrentUser()?.id}
        />
      )}
      
      {activeTab === 'docs' && (
        <div className="docs-container">
          <div className="docs-header">
            <h2>文档中心</h2>
          </div>

          <div className="docs-list">
            <div className="doc-item">
              <div className="doc-item-header"><h3>添加游戏指南</h3></div>
              <GameSubmissionGuide />
            </div>
          </div>
          
          {false && (
          <div className="docs-list">
            <div className="doc-item">
              <div className="doc-item-header">
                <h3>添加游戏指南</h3>
              </div>
              
              <div className="doc-content">
                <div className="doc-section">
                  <h4>1. 平台：选择平台</h4>
                  <p>选择您添加游戏所属的平台，例如：GBA</p>
                </div>
                
                <div className="doc-section">
                  <h4>2. 使用非标准模拟器</h4>
                  <p>如果不勾选此项，每个平台的游戏会以默认模拟器运行；对于IOS，Windows内置，其原理并非游戏模拟原游戏文件运行，而依靠复刻或重编译网页版游戏的板块，默认作为html处理。该选项一般不需要勾选，但如果您添加的rom需要与平台不同的模拟器来运行（举例：「半条命」归类为经典PC游戏更容易被检索，但此版本实为web而非DOS框架下的游戏），请勾选「使用非标准模拟器」为游戏指定模拟器，来覆盖我们的默认选项。</p>
                </div>
                
                <div className="doc-section">
                  <h4>3. 标题</h4>
                  <p>请根据本游戏的Wikipedia条目填写游戏的英文标题，对于中文标题，应当尽量选择在中国大陆流行的标题，若没有大陆译名，可以采用港台译名。如果游戏没有中文名，可自行翻译。对于在国外没有知名度的中国游戏，可不用填写英文名，只填写中文标题。对于于日本首发但从未在欧美地区发行的游戏，如果其有被广为接受的英文标题或拼写，该标题可以采用。</p>
                </div>
                
                <div className="doc-section">
                  <h4>4. 描述</h4>
                  <p>描述一栏暂时无需填写。</p>
                </div>
                
                <div className="doc-section">
                  <h4>5. 额外提示</h4>
                  <p>大部分游戏在平台中添加的“控制提示”已经足以提供，但如果本游戏有着特殊的操作须知项或建议，则在本处填写，将会在控制面板最上面显示。</p>
                </div>
                
                <div className="doc-section">
                  <h4>6. 发行年份，开发商，发行商</h4>
                  <p>请根据Wikipedia条目，填写游戏的发行年份（如果游戏在不同国家/地区有不同发行年份，请填写最早的发行年份），开发者与发行商（如果开发商与发行商有广为接受的中文译名（例如任天堂，索尼），请使用中文名；如果没有正式中文译名，请统一使用英文名）。注意信息一定对应游戏的正确版本（很多游戏在多平台以同样的名称发售，但发行时间，开发商和发行商可能各有不同）。对于参与游戏设计及开发的人明确少于等于三人的早期游戏，鼓励在开发者一栏填写具体人名，用分号隔开。如游戏在不同地区有不同发行商，则以"/"隔开全部列举。</p>
                </div>
                
                <div className="doc-section">
                  <h4>7. 汉化组</h4>
                  <p>对于社区汉化版的游戏rom，请在此填写汉化组名称</p>
                </div>
                
                <div className="doc-section">
                  <h4>8. 加载名称与启动选项</h4>
                  <p>为方便管理，游戏文件在上传后默认会自动更名。但对于基于mame模拟器内核的游戏（包含街机游戏和"掌机"游戏），文件名对游戏正常运行起到重要作用，需在此填入游戏文件包含后缀名的完整名字（例如：<code>frogger.zip</code>），以保证游戏以其原文件名被加载。</p>
                </div>
                
                <div className="doc-section">
                  <h4>9. 多人游戏支持</h4>
                  <p>站点支持两类游戏的联机，一类为同屏幕双控制器的主机游戏（例如魂斗罗），使用EmulatorJS模拟器，一类为可以局域网联机的DOS游戏。如果您上传的游戏为多人联机游戏，请勾选，系统会自动配置。</p>
                </div>
                
                <div className="doc-section">
                  <h4>10. 移动端优化</h4>
                  <p>使用EmulatorJS模拟器的主机或掌机游戏可以将该选项保留勾选。如是其他游戏则需要衡量移动端可否操控，特别是大部分没有专门配置的PC游戏，应被取消勾选。</p>
                </div>
                
                <div className="doc-section">
                  <h4>11. 标签系统</h4>
                  <p>目前标签系统仍未完全实施，请勾选1-2项相关的标签，不要过度勾选。</p>
                </div>
                
                <div className="doc-section">
                  <h4>12. 上传封面图</h4>
                  <p>对于有容易识别的实体卡带等游戏载体的主机，倾向于使用载体的正面图片作为封面图。对于PC游戏等，使用盒子正面图作为封面图。对于街机游戏，倾向于使用其正面照片。对于flash游戏，采用其低清截图。</p>
                  <p>对于有多地区或多版本的游戏，优先使用美国发行的图片作为封面图，若其没有在美国发行过，则使用其他版本。这意味着对于nes/fc或snes/sfc或genesis/md等多区域主机，优先使用美版图片，若无美版发售则使用日版。</p>
                  <p>图片要求最长边分辨率像素不超过1200，最短边分辨率像素不低于300，格式使用jpg或png。如图片大小大于100kb，则建议勾选"tinypng压缩"选项对图片进行自动压缩。</p>
                  <p>封面图推荐数据源为 <a href="https://gamesdb.launchbox-app.com" target="_blank" rel="noopener noreferrer">https://gamesdb.launchbox-app.com</a>，依游戏类型使用CART-FRONT或BOX-FRONT图片，请务必在使用前确定其分辨率在要求范围内，如不是需要使用图片编辑工具在同长宽比下降低分辨率。</p>
                  <p>如所需游戏无法在Launchbox DB上找到，则推荐在谷歌进行该游戏 (如需卡带照片可加cart搜索词) 的图片模式搜索，寻找图片较为清晰，角度相对正面，光照较好的图片。推荐使用photoshop或 <a href="https://www.photopea.com/" target="_blank" rel="noopener noreferrer">https://www.photopea.com/</a> 进行编辑。对于分辨率调整，推荐在使用工具栏的"裁剪"工具框定边缘后使用图像选项卡下的"图像大小"选单，锁定长宽比下调整分辨率。对于角度不正的游戏，推荐使用"编辑"选项卡，"变换"选项卡下的扭曲工具，及必要时使用"变形"工具完成。对于抠图工作，推荐使用"选择主体"工具，"魔棒"工具，"磁性套索"，"多边形套索"，与"套索"工具进行调整。</p>
                </div>
                
                <div className="doc-section">
                  <h4>13. 上传游戏ROM和启动选项</h4>
                  <p className="doc-warning">*请务必在添加任何条目后测试其可否正常运行</p>
                  <p>您将在此处上传游戏ROM或者游戏依赖运行的文件或文件包。对于大部分游戏，您可以在谷歌直接搜索「游戏名+平台+rom」（举例：对于GBA游戏Sonic Advance，您可以在谷歌搜索框输入"sonic advance gba rom"），搜索结果中会涵盖以下几个rom网站：wowroms, romsgames, romsfun, emu-land，一般来说，这几个网站就够用了，请在网站上下载游戏rom并标明版本上传。</p>
                  <p>如果该游戏在多地区有明显不同的发行版本，推荐上传多个地区版本（选择"多rom"模式并添加和标明版本，如有选项中未涵盖的则可以选择自定义手动填写）。</p>
                  <p>如游戏中有大量文本量，您需要尽可能同时上传游戏的汉化版ROM（如果可以找到）。推荐在谷歌，百度中检索，或前往 <a href="https://www.oldmantvg.net/" target="_blank" rel="noopener noreferrer">https://www.oldmantvg.net/</a> 寻找。部分汉化游戏需要使用原版ROM替换字库，请按汉化组教程操作即可。如添加汉化版请如前所述致谢汉化组。</p>
                  <p>对于使用EmulatorJS的游戏平台（及所有主机和单独列出的掌机），您下载的rom常常使用zip格式，即将游戏rom文件嵌套进了一个压缩包中。我们的系统以为此优化，可以直接上传zip文件无需解压。但如您在测试游戏后发现无法运行，请测试解压并提取rom文件本身进行上传。常见的rom后缀名如下：</p>
                  <ul>
                    <li>Atari 2600：<code>.bin</code> <code>.a26</code></li>
                    <li>Famicom / NES：<code>.nes</code> <code>.fds</code></li>
                    <li>Mega Drive / Genesis：<code>.bin</code> <code>.md</code> <code>.gen</code></li>
                    <li>Game Boy Advance：<code>.gba</code></li>
                    <li>Apple II：<code>.woz</code></li>
                    <li>SNES / Super Famicom：<code>.smc</code> <code>.sfc</code></li>
                    <li>Game Boy：<code>.gb</code></li>
                    <li>Virtual Boy：<code>.vb</code></li>
                    <li>Nintendo 64：<code>.z64</code> <code>.n64</code> <code>.v64</code></li>
                  </ul>
                  
                  <div className="doc-subsection">
                    <h5>HTML游戏</h5>
                    <p>对于HTML游戏（目前IOS平台，Windows内置平台默认使用），其支持以单个html文件上传或以主html文件处于根目录的zip压缩包上传。如果使用zip压缩包上传，即需要在"启动选项"以如下形式进行填写：<code>--mainhtml=xxxx.html</code>，来指定主html文件。若需要设置非全屏模式下的分辨率，则输入<code>--resolution=????x???</code>进行设置。</p>
                  </div>
                  
                  <div className="doc-subsection">
                    <h5>MAME游戏</h5>
                    <p>对于MAME游戏（目前街机平台及"掌机"平台使用），请在上传.zip格式rom的同时在"加载名称"项填写含扩展名的完整文件名。</p>
                    <p>对于街机游戏，您可以通过与其他游戏平台rom下载的类似方法在互联网进行检索下载，但如果您测试游戏仍不正常运行，可能是该rom与本版本的MAME不适配，则建议前往internet archive的mame2003+reference set中展开右侧ZIP项并通过CTRL-F搜索文件名获取 <a href="https://archive.org/details/mame-2003-plus-reference-set" target="_blank" rel="noopener noreferrer">https://archive.org/details/mame-2003-plus-reference-set</a>。</p>
                    <p>对于“掌机”游戏，需要正确配置游戏分辨率、驱动以及对应的 JavaScript 与 WebAssembly 依赖。例如：<code>--driver=tapollo13 --resolution=733x540 --js=mamehh_sm510.js --wasm=mamehh_sm510.wasm</code>。如不确定所需文件或配置，请联系管理员。</p>
                  </div>
                  
                  <div className="doc-subsection">
                    <h5>DOS游戏</h5>
                    <p>对于DOS（经典PC）游戏，请前往 <a href="https://dos.zone/studio-v8" target="_blank" rel="noopener noreferrer">DOS.Zone Bundle Creator</a> 创建符合 JS-DOS 格式的游戏包，并配置启动程序与所需的移动端控制。</p>
                  </div>
                  
                  <div className="doc-subsection">
                    <h5>Flash游戏</h5>
                    <p>对于Flash游戏，可以上传您合法持有的单个.swf文件。多文件Flash游戏请将运行所需内容放在压缩包根目录，并使用启动选项 <code>--mainfile=xxx.swf</code> 指定主文件。</p>
                  </div>
                  
                  <div className="doc-subsection">
                    <h5>苹果II游戏</h5>
                    <p>对于苹果II游戏，目前仅支持单.woz格式的游戏，功能待扩充。</p>
                  </div>
                </div>
                
                <div className="doc-section">
                  <h4>14. 上传游戏手册</h4>
                  <p>请下载游戏手册的pdf文件，并在写清文件名（如果有多版本，请命名区分各版本的文件，如无，则填"手册"即可）后上传。一般来说，可以直接在谷歌搜索框中搜索「游戏名+平台名+manual pdf」（举例：对于GBA游戏sonic advance，请在谷歌搜索"sonic advance gba manual pdf"），大部分主流平台游戏的手册都可以在internet archive中直接下载，对于一些小众游戏，可能需要费心寻找。</p>
                  <p>如果pdf文件过大，请寻找pdf压缩网站压缩pdf后再上传，推荐使用 <a href="https://www.ilovepdf.com/compress_pdf" target="_blank" rel="noopener noreferrer">https://www.ilovepdf.com/compress_pdf</a>。上传pdf文件最大不得超过25mb。</p>
                  <p>为了方便可读性，手册文件不允许两页在pdf中被扫描成一页。如果pdf文件如此，则需要裁剪（一般情况不需要，网站的pdf阅读器会自动裁剪）。可使用工具 <a href="https://online2pdf.com/en/cut-pdf-pages-into-halves" target="_blank" rel="noopener noreferrer">https://online2pdf.com/en/cut-pdf-pages-into-halves</a>，Cutting Direction选择"Horizontal - From left to right", Page Layout 使用 Original Size。如果只有部分页面被两页合并扫描，而工具处理后有的页面被分割了的话，可以使用 <a href="https://www.ilovepdf.com/organize-pdf" target="_blank" rel="noopener noreferrer">https://www.ilovepdf.com/organize-pdf</a>，上传分割后和分割前的pdf文件进行组合。</p>
                </div>
                
                <div className="doc-section doc-appendix">
                  <h4>附录</h4>
                  
                  <div className="doc-subsection">
                    <h5>1. 文件准备</h5>
                    <p>请只提交您有权提供的游戏与资料文件，并按照对应模拟器的要求准备完整、可运行的文件包。</p>
                  </div>
                  
                  <div className="doc-subsection">
                    <h5>2. 魔法上网</h5>
                    <p>您可能需要魔法上网以访问部分需要的网站，该问题请联系管理员。另外，如果您在境外，为了保证上传游戏文件速度，可能也需要魔法上网回国服务，请联系管理员。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Manage; 
